/* eslint-disable no-console, @typescript-eslint/no-explicit-any */
import get from "lodash/get";
import mapValues from "lodash/mapValues";

import {
  featureFlagDefinitions,
  featureFlags,
  type FeatureFlagDefinition,
  type FeatureFlags,
  type FeatureFlagsEnum,
} from "../config/featureFlags";

type FeatureFlagOverrides = Partial<FeatureFlags>;
let featureFlagOverrides: FeatureFlagOverrides = {};
type LocationApi = Pick<Location, "assign" | "reload" | "pathname" | "search" | "hash">;

export type FeatureFlagStatus = {
  before: boolean;
  dependencies: readonly FeatureFlagsEnum[];
  description?: string;
  override?: boolean;
  result: boolean;
};

export const isDevelopment = process.env.REACT_APP_STAGE === "development";
export const isStaging = process.env.REACT_APP_STAGE === "staging";
export const isDevelopmentOrStaging = isDevelopment || isStaging;

const isFeatureFlagKey = (key: string): key is FeatureFlagsEnum => key in featureFlagDefinitions;

type FeatureFlagDependencyDefinitions<TFeatureFlagName extends string> = Record<
  TFeatureFlagName,
  Pick<FeatureFlagDefinition<TFeatureFlagName>, "dependencies">
>;
type FeatureFlagDependencyLoopLogger = Pick<Console, "warn">;

/**
 * Resolves the complete feature-flag activation chain for a target flag.
 *
 * The returned list includes the requested flag followed by its dependencies in depth-first order. Flags
 * already resolved through another branch are skipped so shared dependencies are only enabled once. If a
 * dependency loop is detected, the active branch is stopped and logged while the rest of the dependency graph
 * continues resolving.
 */
export const resolveFeatureFlagsToEnable = <TFeatureFlagName extends string>(
  featureFlagName: TFeatureFlagName,
  definitions: FeatureFlagDependencyDefinitions<TFeatureFlagName>,
  logger: FeatureFlagDependencyLoopLogger = console,
): TFeatureFlagName[] => {
  const flagsToEnable: TFeatureFlagName[] = [];
  const resolvedFlags = new Set<TFeatureFlagName>();
  const resolvingFlags = new Set<TFeatureFlagName>();

  const visit = (flag: TFeatureFlagName, path: TFeatureFlagName[]) => {
    if (resolvingFlags.has(flag)) {
      const loopStartIndex = path.indexOf(flag);
      const loopPath = [...path.slice(Math.max(loopStartIndex, 0)), flag].join(" -> ");

      logger.warn(
        `Feature flag dependency loop detected: ${loopPath}. Stopping activation for this dependency chain.`,
      );
      return;
    }

    if (resolvedFlags.has(flag)) {
      return;
    }

    resolvingFlags.add(flag);
    flagsToEnable.push(flag);

    definitions[flag].dependencies?.forEach((dependency) => {
      visit(dependency, [...path, flag]);
    });

    resolvingFlags.delete(flag);
    resolvedFlags.add(flag);
  };

  visit(featureFlagName, []);

  return flagsToEnable;
};

const getFeatureFlagDependenciesToSet = (featureFlagName: FeatureFlagsEnum): FeatureFlagsEnum[] =>
  resolveFeatureFlagsToEnable(featureFlagName, featureFlagDefinitions).filter(
    (dependency) => dependency !== featureFlagName,
  );

export const getFeatureFlagDependenciesToDisable = (featureFlagName: FeatureFlagsEnum): FeatureFlagsEnum[] =>
  getFeatureFlagDependenciesToSet(featureFlagName).filter((dependency) => !featureFlags[dependency]);

const sanitizeOverrides = (overrides: unknown): FeatureFlagOverrides => {
  if (typeof overrides !== "object" || overrides === null || Array.isArray(overrides)) {
    return {};
  }

  return Object.entries(overrides).reduce((result, [key, value]) => {
    if (isFeatureFlagKey(key) && typeof value === "boolean") {
      result[key] = value;
    }

    return result;
  }, {} as FeatureFlagOverrides);
};

const hydrateOverrides = () => {
  const flagOverridesString = (localStorage && localStorage.getItem("featureFlagOverrides")) ?? "{}";

  let parsedFlagOverrides: unknown;
  try {
    parsedFlagOverrides = JSON.parse(flagOverridesString) ?? {};
  } catch {
    parsedFlagOverrides = {};
  }

  const flagOverrides = sanitizeOverrides(parsedFlagOverrides);
  const sanitizedOverridesString = JSON.stringify(flagOverrides);

  if (flagOverridesString !== sanitizedOverridesString) {
    localStorage.setItem("featureFlagOverrides", sanitizedOverridesString);
  }

  featureFlagOverrides = flagOverrides;
};

export const getFeatureFlagWithOverrides = () => ({ ...featureFlags, ...featureFlagOverrides });

export const getFeatureFlagDependenciesToEnable = (featureFlagName: FeatureFlagsEnum): FeatureFlagsEnum[] => {
  const currentFeatureFlags = getFeatureFlagWithOverrides();
  return getFeatureFlagDependenciesToSet(featureFlagName).filter(
    (dependency) => !currentFeatureFlags[dependency],
  );
};

export default function hasFeatureFlag(featureFlagName: FeatureFlagsEnum): boolean {
  const currentFeatureFlags = getFeatureFlagWithOverrides();
  return get(currentFeatureFlags, featureFlagName, false);
}

export function getFeatureFlagStatuses() {
  const resultFlags = getFeatureFlagWithOverrides();
  return Object.keys(featureFlags).reduce((result, _key) => {
    const key = _key as FeatureFlagsEnum;

    const row = {
      before: featureFlags[key],
      dependencies: featureFlagDefinitions[key].dependencies ?? [],
      description: featureFlagDefinitions[key].description,
      override: featureFlagOverrides[key],
      result: resultFlags[key],
    };

    return { ...result, [key]: row };
  }, {} as { [K in FeatureFlagsEnum]: FeatureFlagStatus });
}

const report = () => {
  const reportValues = getFeatureFlagStatuses();

  console.info(
    "%c🧪 Current flag overrides",
    "background-color: lightgreen; color: #111111; padding: 4px 12px; border-radius: 4px;",
  );
  console.table(reportValues);
};

const reloadWithOptionalDestination = (destination?: string, locationApi: LocationApi = window.location) => {
  const currentLocation = `${locationApi.pathname}${locationApi.search}${locationApi.hash}`;

  if (destination && destination !== currentLocation) {
    locationApi.assign(destination);
    return;
  }

  // IDK why but this line is important for localization to initialize for unit tests.
  // Disable this line and remove the import then run unit tests to see what I mean.
  locationApi.reload();
};

const storeFeatureFlagOverride = (
  newFeatureFlags: FeatureFlagOverrides,
  destination?: string,
  locationApi?: LocationApi,
) => {
  const sanitizedFeatureFlags = sanitizeOverrides(newFeatureFlags);

  featureFlagOverrides = sanitizedFeatureFlags;
  localStorage.setItem("featureFlagOverrides", JSON.stringify(sanitizedFeatureFlags));
  report();
  reloadWithOptionalDestination(destination, locationApi);
};

const set = (
  featureFlagName: FeatureFlagsEnum,
  state: boolean,
  destination?: string,
  locationApi?: LocationApi,
) => {
  const flagsToSet = state
    ? resolveFeatureFlagsToEnable(featureFlagName, featureFlagDefinitions)
    : [featureFlagName];
  setMultiple(flagsToSet, state, destination, locationApi);
};

const setMultiple = (
  featureFlagNames: readonly FeatureFlagsEnum[],
  state: boolean,
  destination?: string,
  locationApi?: LocationApi,
) => {
  const newFlags = featureFlagNames.reduce((result, flag) => {
    result[flag] = state;
    return result;
  }, {} as FeatureFlagOverrides);
  const newFeatureFlagOverrides = { ...featureFlagOverrides, ...newFlags };
  storeFeatureFlagOverride(newFeatureFlagOverrides, destination, locationApi);
};

export const enableAll = () => {
  storeFeatureFlagOverride(mapValues(featureFlags, () => true));
};

export const resetAll = () => {
  const newFeatureFlagOverrides = {};
  storeFeatureFlagOverride(newFeatureFlagOverrides);
};

const reset = (featureFlagName: FeatureFlagsEnum) => {
  const newFeatureFlagOverrides = { ...featureFlagOverrides };
  delete newFeatureFlagOverrides[featureFlagName];
  storeFeatureFlagOverride(newFeatureFlagOverrides);
};

export const setTrue = (featureFlagName: FeatureFlagsEnum) => set(featureFlagName, true);
export const setFalse = (featureFlagName: FeatureFlagsEnum) => set(featureFlagName, false);
export const setFalseForFeatureFlags = (featureFlagNames: readonly FeatureFlagsEnum[]) =>
  setMultiple(featureFlagNames, false);
export const setFeatureFlagOverrideAndReload = (
  featureFlagName: FeatureFlagsEnum,
  state: boolean,
  destination?: string,
  locationApi?: LocationApi,
) => set(featureFlagName, state, destination, locationApi);

const windowFeatureFlags = {
  report,
  override: {
    set,
    setTrue,
    setFalse,
    reset,
    resetAll,
  },
};

declare global {
  interface Window {
    featureFlags: typeof windowFeatureFlags;
  }
}

hydrateOverrides();

export const initFeatureFlagOverrides = async (): Promise<void> => {
  window.featureFlags = windowFeatureFlags;

  console.groupCollapsed(
    "%c🧪 Feature Flag Overrides available",
    "background-color: lightgreen; color: #111111; padding: 4px 12px; border-radius: 4px;",
    "(window.featureFlags)",
  );
  report();
  console.info(window.featureFlags);
  console.groupEnd();
};
